<template>
  <div class="viewOrder">
    <Loading v-if="loading" />
    <div class="viewOrder__link">
      <Link :title="$t('backToOrdersHistory')" @back-to="backTo" />
    </div>
    <div class="viewOrder__header">
      <Title :title="$t('viewOrder')" />
      <div v-if="viewOrder?.paymentInfo" class="payment">
        <p
          :class="{
            paymentTitle: true,
            paymentPending: viewOrder.paymentInfo.status === 'pending',
            paymentFailed:
              viewOrder.paymentInfo.status === 'failed' ||
              viewOrder.paymentInfo.status === 'cancelled',
            paymentCompleted:
              viewOrder.paymentInfo.status === 'completed' ||
              viewOrder.paymentInfo.status === 'done'
          }"
        >
          {{ getPaymentStatus(viewOrder.paymentInfo.status) }}
        </p>
      </div>
    </div>
    <div class="viewOrder__main">
      <div>
        <div v-if="viewOrder?.callReason?.length" class="info">
          <p>{{ $t('reasonForTransaction') }}</p>
          <span>
            {{ getString(viewOrder?.callReason) }}
          </span>
        </div>
        <div v-if="viewOrder?.orderType" class="info">
          <p>{{ $t('orderPayment') }}</p>
          <span> {{ getString(viewOrder?.orderType) }}</span>
        </div>
        <div class="info">
          <p>{{ $t('medicalAdviceGiven') }}</p>
          <span>{{ getConsultation(viewOrder?.consultation) }}</span>
        </div>
        <div v-if="viewOrder?.orderNumber" class="info">
          <p>{{ $t('orderNumber') }}</p>
          <span>{{ viewOrder?.orderNumber }}</span>
        </div>
        <div v-if="viewOrder?.comment" class="info">
          <p>{{ $t('internalNote') }}</p>
          <span>{{ viewOrder?.comment }}</span>
        </div>
        <div v-if="viewOrder?.deliveryMethod" class="info">
          <p>{{ $t('deliveryMethod') }}</p>
          <span>{{ getDeliveryMethod(viewOrder.deliveryMethod) }}</span>
        </div>
        <div class="info">
          <p>{{ $t('deliveryContentMatchesOrder') }}</p>
          <span
            v-if="!viewOrder?.confirmed"
            :class="{ confirm: !viewOrder?.confirmed }"
            @click="openConfirmation"
          >
            {{ $t('confirm') }}
          </span>
          <div v-else class="content">
            <p>{{ $t('confirmedDy') }} {{ viewOrder?.confirmedUserName }}</p>
            <span> ({{ dateTime(viewOrder?.confirmedTime) }})</span>
          </div>
        </div>
        <div v-if="viewOrder?.handler" class="info">
          <p>{{ $t('handler') }}</p>
          <span>{{ viewOrder.handler }}</span>
        </div>
      </div>
      <ClientInfo :view-order="viewOrder" />
    </div>
    <Button
      v-if="viewOrder?.deliveryMethod !== 'homeDelivery'"
      class="viewOrder__button"
      :variant="viewOrder?.deliveryId ? 'disabled' : 'filled'"
      :title="$t('reserve')"
      :disabled="viewOrder?.deliveryId ? true : false"
      @click="reserve"
    />
    <PaymentInfo :payment-info="viewOrder?.paymentInfo" />
  </div>
  <OpenConfirmation
    :show-open-confirmation="showOpenConfirmation"
    @close-modal="showOpenConfirmation = false"
    @confirm-request="confirmRequest"
  />
  <div
    v-if="viewOrder?.paymentInfo && viewOrder?.paymentInfo.status === 'pending'"
    class="cancelPaymentRequest"
  >
    <Footer
      :payment-notification-was-successfully-resend="
        paymentNotificationWasSuccessfullyResend
      "
      :payment-info="viewOrder.paymentInfo"
      @cancel-payment="cancelPayment"
      @resend-payment-notification-result="resendPaymentNotificationResult"
    />
  </div>
  <QuickReservation
    v-if="showModalQuickReservation"
    :show-modal-quick-reservation="showModalQuickReservation"
    :view-order-data="viewOrder"
    @close-modal="closeModalQuickReservation"
  />
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import moment from 'moment';
import router from '@/router';

import Loading from '@/views/Loading.vue';
import ClientInfo from './ClientInfo/ClientInfo.vue';
import OpenConfirmation from '../OpenConfirmation/OpenConfirmation.vue';
import PaymentInfo from './PaymentInfo/PaymentInfo.vue';
import QuickReservation from '@/duplicatePages/QuickReservation/QuickReservation.vue';

import { Params } from '@/store/modules/users/types';

@Options({
  name: 'ViewOrder',
  components: {
    ClientInfo,
    OpenConfirmation,
    PaymentInfo,
    QuickReservation,
    Loading
  },
  data() {
    return {
      dataSSN: '',
      loading: false,
      showOpenConfirmation: false,
      showModalQuickReservation: false,
      showDecodedSSN: false,
      updatePage: false,
      paymentNotificationWasSuccessfullyResend: false
    };
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('orders', ['viewOrder', 'decodedSSN'])
  },
  created() {
    this.show();
  },
  methods: {
    ...mapActions('orders', [
      'getViewOrderById',
      'getConfirmContent',
      'getOrdersHistoryDecodedSSN',
      'resendPaymentNotification',
      'cancelPaymentRequest'
    ]),
    closeModalQuickReservation() {
      this.loading = true;

      this.showModalQuickReservation = false;
      this.updatePage = true;
      this.show();

      this.loading = false;
    },
    async show() {
      this.loading = true;
      const { id, pharmacyId } = this.$route.params;

      await this.getViewOrderById({
        pharmacyId,
        orderId: id
      });

      this.loading = false;
    },
    async initSSN() {
      const { pharmacyId } = this.$route.params;

      const params: Params = {
        pharmacyId: pharmacyId,
        orderId: this.viewOrder.id
      };

      this.showDecodedSSN = !this.showDecodedSSN;

      if (this.showDecodedSSN) {
        this.loading = true;

        await this.getOrdersHistoryDecodedSSN(params);

        this.dataSSN = this.decodedSSN?.ssnOnBehalf;
        this.loading = false;
      }
    },
    backTo() {
      router.push({ name: 'OrdersHistory' });
    },
    getPaymentStatus(item: string): unknown {
      const labels: Record<string, unknown> = {
        pending: this.$t('pending'),
        failed: this.$t('failed'),
        completed: this.$t('completed'),
        done: this.$t('doneUpperCase'),
        cancelled: this.$t('cancelledTableUpperCase')
      };

      return labels[item];
    },
    getConsultation(consultation: boolean) {
      return consultation ? `${this.$t('yes')}` : `${this.$t('no')}`;
    },
    getString(array: Array<string>) {
      const type: Record<string, unknown> = {
        selfCare: this.$t('selfCare'),
        prescriptionDrugs: this.$t('prescriptionDrugs'),
        prescription: this.$t('prescription'),
        other: this.$t('other')
      };

      return array?.length
        ? array.map((item: string) => type[item]).join(', ')
        : '';
    },
    getDeliveryMethod(item: string): unknown {
      const labels: Record<string, unknown> = {
        pickupbox: this.$t('pickupBox'),
        pharmacy: this.$t('pharmacy'),
        homeDelivery: this.$t('homeDelivery')
      };

      return labels[item];
    },
    dateTime(value: number) {
      return value ? moment(value).format('DD.MM.YYYY HH:mm') : '';
    },
    openConfirmation() {
      this.showOpenConfirmation = true;
    },
    async confirmRequest() {
      const { pharmacyId } = this.$route.params;

      await this.getConfirmContent({
        pharmacyId: pharmacyId,
        orderId: this.viewOrder.id
      });

      this.updatePage = true;
      this.showOpenConfirmation = false;
    },
    async resendPaymentNotificationResult() {
      if (!this.paymentNotificationWasSuccessfullyResend) {
        const { id } = this.$route.params;
        this.loading = true;

        await this.resendPaymentNotification({ orderId: id });

        this.paymentNotificationWasSuccessfullyResend = true;
        this.loading = false;
      }
    },
    async cancelPayment() {
      this.loading = true;

      const orderId = this.viewOrder.id;
      await this.cancelPaymentRequest({ orderId });

      this.updatePage = true;
      this.loading = false;
    },
    reserve() {
      this.showModalQuickReservation = true;
    }
  },
  watch: {
    updatePage() {
      if (this.updatePage) {
        this.show();
      }
    }
  }
})
export default class ViewOrder extends Vue {}
</script>

<style lang="scss" scoped>
.viewOrder {
  display: flex;
  flex-direction: column;
  padding: 24px;
  .viewOrder__link {
    margin-bottom: 27px;
  }
  .viewOrder__header {
    display: flex;
    align-items: center;
    .payment {
      .paymentTitle {
        font-size: $tenSize;
        padding: 6px 8px;
        border-radius: 4px;
        margin-left: 16px;
        font-family: 'Ubuntu-Medium';
        &.paymentPending {
          color: $colorPending;
          background-color: $backgroundPending;
        }
        &.paymentFailed {
          color: $colorFailed;
          background-color: $backgroundFailed;
        }
        &.paymentCompleted {
          color: $colorCompleted;
          background-color: $backgroundCompleted;
        }
      }
    }
  }
  .viewOrder__main {
    display: flex;
    align-items: flex-start;
    margin-top: 32px;
    .info {
      margin-bottom: 24px;
      margin-right: 41px;
      .ssn_btn {
        height: 16px !important;
      }
      .content {
        p {
          display: inline;
          color: $mid-grey;
          font-size: $fourteenSize;
          padding-bottom: 0;
        }
        span {
          color: $label;
          font-size: $fourteenSize;
          line-height: $fourteenSize;
        }
      }
      p {
        color: $label;
        font-size: $twelveSize;
        line-height: $fourteenSize;
        padding-bottom: 7px;
      }
      span {
        color: $mid-grey;
        font-size: $fourteenSize;
      }
      .confirm {
        color: $colorCompleted;
        cursor: pointer;
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
    .main-info {
      width: 377px;
      height: 219px;
      border: 1px solid $border;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
    }
  }
  .viewOrder__button {
    margin-top: 32px;
  }
}
</style>
